import {
  Box,
  Text,
  InputGroup,
  InputLeftElement,
  Input,
  Flex,
  Link,
  Icon,
  Center,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { debounce } from 'lodash-es';

import SearchIcon from '@/icons/Search';
import { useGetVouchJobsQuery } from '@/store/query/vouch';
import NoSearchResult from '@/assets/icons/colored/ResultsEmpty';

import { useVouchManager } from '../vouch-manager';
import { Loading } from '../../Loading';
import { LocationFilter } from './filter/Location';
import { DepartmentFilter } from './filter/Department';

const Jobs = ({ job = null, onSelect = () => {}, selected = null }) => {
  if (!job) return null;
  return (
    <Box
      p="12px 16px"
      cursor="pointer"
      borderRadius="6px"
      _hover={{ bg: 'light.hover' }}
      bg={selected === job?.id ? 'light.hover' : 'white'}
      onClick={() => {
        onSelect(job?.id);
      }}
    >
      <Text size="sm-medium">{job?.name}</Text>
      {job?.offices?.length > 0 && (
        <Text
          variant="muted"
          _after={{ content: '"|"', padding: '0 8px' }}
          display="inline-block"
        >
          {job?.offices?.join(', ')}
        </Text>
      )}
      {job?.departments?.length > 0 && (
        <Text
          variant="muted"
          _after={{ content: '"|"', padding: '0 8px' }}
          display="inline-block"
        >
          {job?.departments?.join(', ')}
        </Text>
      )}
      {job?.employment_type && (
        <Text variant="muted" display="inline-block">
          {job?.employment_type}
        </Text>
      )}
    </Box>
  );
};

const defaultFilter = { office: '', department: '' };
export const VouchForJob = ({
  departments = [],
  offices = [],
  isAtsIntegrated,
}) => {
  const [searchTerm, setSearchTerm] = useState(null);
  const [filter, setFilter] = useState(defaultFilter);
  const userInput = useRef();
  const { data, isLoading } = useGetVouchJobsQuery(
    { searchTerm, filter },
    { skip: !isAtsIntegrated }
  );
  const { selectedJob, onSelectJob } = useVouchManager();

  const onSearchUser = debounce(() => {
    setSearchTerm(userInput?.current?.value);
  }, 300);

  const onLocationFilter = (office) => {
    setFilter((prev) => ({ ...prev, office: office?.key }));
  };

  const clearFilter = () => {
    setFilter(defaultFilter);
  };

  const onDepartmentFilter = (department) => {
    setFilter((prev) => ({ ...prev, department: department?.key }));
  };

  useEffect(() => {
    if (isLoading) return;
    onSelectJob(data?.ats_jobs?.[0]?.id);
  }, [data, isLoading]); //eslint-disable-line

  if (isLoading) return <Loading size={8} />;
  return (
    <>
      <Flex>
        {offices?.length > 0 && (
          <LocationFilter
            locations={offices}
            filter={filter}
            onSelect={onLocationFilter}
          />
        )}
        {departments?.length > 0 && (
          <DepartmentFilter
            departments={departments}
            filter={filter}
            onSelect={onDepartmentFilter}
          />
        )}
      </Flex>
      {(filter?.department || filter?.office) && (
        <Link
          display="block"
          my="8px"
          color="light.text-secondary"
          fontSize="14px"
          textDecor="underline"
          onClick={clearFilter}
        >
          Clear Filters
        </Link>
      )}
      {isAtsIntegrated ? (
        <>
          <Box mt="12px">
            <InputGroup>
              <InputLeftElement
                pointerEvents="none"
                children={<SearchIcon />}
              />
              <Input
                placeholder="Search"
                onChange={onSearchUser}
                ref={userInput}
              />
            </InputGroup>
          </Box>

          {data?.ats_jobs?.length > 0 ? (
            <Box height="400px" overflow="auto" mt="12px">
              {data?.ats_jobs?.map((job) => (
                <Jobs
                  key={job?.id}
                  job={job}
                  onSelect={onSelectJob}
                  selected={selectedJob}
                />
              ))}
            </Box>
          ) : (
            <Box textAlign="center">
              <Icon as={NoSearchResult} width="200px" height="200px" />
              <Text size="sm-medium">No search results found</Text>
              <Text variant="muted">
                We couldn't find anything for your query. Try making your search
                broader.
              </Text>
            </Box>
          )}
        </>
      ) : (
        <Center
          height="560px"
          textAlign="center"
          color="#696B6C"
          fontSize="14px"
        >
          We could not find an ATS integration with this Kula workspace
        </Center>
      )}
    </>
  );
};
